import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { nfmt, usd, credits, fmtDateTime } from "@/lib/adminMeta";
import { ArrowLeft, DollarSign, Sparkles, Cpu } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground text-sm"><Icon className="w-4 h-4 text-gold" /> {label}</div>
    <div className="text-2xl font-bold text-navy mt-2">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function AdminUsageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/usage/dealers/${id}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [id]);

  if (data === false) return <div className="text-muted-foreground">Dealership not found.</div>;
  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const recs = data.records || [];

  return (
    <div className="space-y-6 fade-up" data-testid="admin-usage-detail">
      <button onClick={() => navigate("/admin/usage")} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy" data-testid="usage-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to Usage
      </button>

      <div>
        <h1 className="text-3xl font-bold text-navy">{data.name}</h1>
        <p className="text-muted-foreground mt-1">AI campaign generation history and estimated cost.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={DollarSign} label={`Cost (${data.month_label})`} value={usd(data.this_month.cost.total_usd)} sub={credits(data.this_month.cost.credits)} testid="detail-cost-month" />
        <StatCard icon={DollarSign} label="Cost (all time)" value={usd(data.all_time.cost.total_usd)} sub={credits(data.all_time.cost.credits)} testid="detail-cost-all" />
        <StatCard icon={Sparkles} label="Generations (all time)" value={nfmt(data.all_time.generations)} testid="detail-generations" />
        <StatCard icon={Cpu} label="Tokens (all time)" value={nfmt(data.all_time.total_tokens)} sub={`${nfmt(data.all_time.input_tokens)} in · ${nfmt(data.all_time.output_tokens)} out`} testid="detail-tokens" />
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-navy">Individual generations</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-navy">
              <tr className="text-left">
                {["Date / Time", "User", "Input tokens", "Output tokens", "Total tokens", "Est. cost", "Est. credits"].map((h) => (
                  <th key={h} className="px-5 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-testid="detail-records-body">
              {recs.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">No generations recorded for this dealership yet.</td></tr>
              ) : recs.map((r) => (
                <tr key={r.id} className="hover:bg-secondary/40" data-testid={`detail-record-${r.id}`}>
                  <td className="px-5 py-3 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                  <td className="px-5 py-3 whitespace-nowrap">{r.user_email || "-"}</td>
                  <td className="px-5 py-3">{nfmt(r.input_tokens)}</td>
                  <td className="px-5 py-3">{nfmt(r.output_tokens)}</td>
                  <td className="px-5 py-3">{nfmt(r.total_tokens)}</td>
                  <td className="px-5 py-3 font-semibold text-navy">{usd(r.cost.total_usd)}</td>
                  <td className="px-5 py-3 text-muted-foreground">{credits(r.cost.credits)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground" data-testid="detail-estimate-note">
        Estimates based on the currently configured Emergent Universal Key rates
        ({usd(data.rates.input_usd_per_1m)}/1M input, {usd(data.rates.output_usd_per_1m)}/1M output, 1 credit = {usd(data.rates.credit_usd)}).
        Actual charges may differ if provider pricing changes.
      </p>
    </div>
  );
}
